import React, { useState, useEffect, useCallback } from "react";
import { CampaignCharts } from "../../features/campaign";
import { campaignsService, CampaignAdapter } from "../../services";
import { Campaign } from "../../data/campaignData";

const Dashboard: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const loadCampaigns = useCallback(async (forceReload = false) => {
    try {
      setIsLoading(true);
      setError("");

      // Add timestamp to prevent caching
      const timestamp = Date.now();
      console.log(
        `Loading campaigns data at ${new Date(timestamp).toLocaleTimeString()}`
      );

      const apiCampaigns = await campaignsService.getCampaigns();
      // Convert API campaigns to legacy format for charts
      const legacyCampaigns = CampaignAdapter.toLegacyArray(apiCampaigns);
      setCampaigns(legacyCampaigns);

      console.log(
        `Loaded ${legacyCampaigns.length} campaigns:`,
        legacyCampaigns.map((c) => ({ name: c.name, status: c.status }))
      );
    } catch (error: any) {
      console.error("Failed to load campaigns:", error);
      setError(error.message || "Không thể tải dữ liệu chiến dịch");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data when component mounts (when navigating to dashboard)
  useEffect(() => {
    console.log("Dashboard component mounted - loading fresh data");
    loadCampaigns(true);
  }, [loadCampaigns]);

  // Only reload when component mounts (when navigating to dashboard)
  // Removed visibility and focus listeners to avoid unnecessary API calls

  // Listen for custom events from other pages when data changes
  useEffect(() => {
    const handleDataChange = () => {
      console.log("Data change event received - reloading data");
      loadCampaigns(true);
    };

    window.addEventListener("campaign-data-changed", handleDataChange);
    window.addEventListener("dashboard-refresh", handleDataChange);

    return () => {
      window.removeEventListener("campaign-data-changed", handleDataChange);
      window.removeEventListener("dashboard-refresh", handleDataChange);
    };
  }, [loadCampaigns]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Đang tải dữ liệu dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
            <div className="whitespace-pre-line">{error}</div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-500"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <CampaignCharts campaigns={campaigns} />
    </div>
  );
};

export default Dashboard;
